import formatShopifyMoney from "@theme/shopify-format-money";
import Swiper from "@theme/swiper";
import { Navigation } from "@theme/swiper-modules";
import { debounce } from "@theme/utilities";

export default class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openTrigger = document.querySelector(`#${this.dataset.openTrigger}`);
    this.overlay = this.querySelector("#CustomCartOverlay");
    this.closeButton = this.querySelector("[data-close]");
    this.headerCartCount = document.querySelector(".header-cart-item-count");
    this.cartItemCount = document.querySelector(".cart-item-count");
    this.cartItemWrapper = document.querySelector(
      ".header__icon--cart__wrapper",
    );
  }

  connectedCallback() {
    formatShopifyMoney();
    this.openTrigger?.addEventListener("click", this.handleOpen.bind(this));
    this.closeButton?.addEventListener("click", this.closeDrawer.bind(this));
    document.addEventListener("click", (event) => {
      if (event.target === this.overlay) {
        this.closeDrawer();
      }
    });

    // Once the cart:rerender event is set up, we can call it from everyhwere we want in the theme
    // just dont forget to append the correct data to the event so we can rerender the cart

    document.addEventListener("cart:rerender", this.cartRerender.bind(this));

    if (window.location.pathname === "/cart") {
      this.openDrawer();
    }
  }

  handleOpen(event) {
    event.preventDefault();

    this.openDrawer();
  }

  openDrawer() {
    this.setAttribute("open", "");
    document.body.classList.add("overflow-hidden");
  }

  closeDrawer() {
    this.removeAttribute("open");
    document.body.classList.remove("overflow-hidden");
  }

  cartRerender(event) {
    // We first create empty fake elements so we can store the HTML string of the new section later on
    // This helps with selecting the right elements to replace without reloading the page

    const fakeElement = document.createElement("div");
    const fakeCount = document.createElement("div");
    const newHTML = event.detail.sections["cart-drawer"];
    const newCount = event.detail.sections["custom-cart-count"];

    fakeElement.innerHTML = newHTML;
    fakeCount.innerHTML = newCount;

    const countElement = fakeCount.querySelector(".cart-item-count");
    const countValue = countElement
      ? Number(countElement.textContent.trim())
      : 0;

    this.querySelector(".custom-cart-drawer__inner").innerHTML =
      fakeElement.querySelector(".custom-cart-drawer__inner").innerHTML;

    this.headerCartCount.innerHTML =
      fakeCount.querySelector(".cart-item-count").innerHTML;

    this.cartItemCount.innerHTML =
      fakeCount.querySelector(".cart-item-count").innerHTML;

    countValue > 0
      ? this.cartItemWrapper.classList.remove("hidden")
      : this.cartItemWrapper.classList.add("hidden");
    this.openDrawer();
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartActions extends HTMLElement {
  constructor() {
    super();

    this.plusButton = this.querySelector("[data-plus]");
    this.minusButton = this.querySelector("[data-minus]");
    this.removeButton = this.querySelector("[data-remove]");
    this.loading = this.closest(".cart-drawer__item").querySelector(
      ".cart-drawer__item-loading",
    );
    this.handleChange = this.handleChange.bind(this);
    this.errorMessage =
      this.closest(".cart-drawer__item").querySelector(".error-message");

    this.debouncedChangeQuantity = debounce(
      this.changeQuantity.bind(this),
      500,
    );
  }

  connectedCallback() {
    this.plusButton.addEventListener("click", (event) =>
      this.handleChange(event, "plus"),
    );
    this.minusButton.addEventListener("click", (event) =>
      this.handleChange(event, "minus"),
    );
    this.removeButton.addEventListener("click", (event) =>
      this.handleRemove(event),
    );
  }

  disconnectedCallback() {
    this.plusButton.removeEventListener("click", this.handleChange);
    this.minusButton.removeEventListener("click", this.handleChange);
    this.removeButton.removeEventListener("click", this.handleRemove);
  }

  handleChange(event, action) {
    const quantityElement = event.target
      .closest(".quantity-selector")
      .querySelector(".quantity-selector__value");

    let quantity = parseInt(quantityElement.textContent);

    if (action === "plus") {
      quantity += 1;
    } else if (action === "minus") {
      if (quantity > 1) {
        quantity -= 1;
      } else {
        return; // don't go below 1
      }
    }

    quantityElement.textContent = quantity;

    const formData = {
      line: this.dataset.line,
      quantity: quantity,
      sections: "cart-drawer,custom-cart-count",
    };

    this.debouncedChangeQuantity(formData);
  }

  handleRemove() {
    const formData = {
      line: this.dataset.line,
      quantity: 0,
      sections: "cart-drawer,custom-cart-count",
    };
    this.changeQuantity(formData);
  }

  changeQuantity(formData) {
    this.loading.classList.remove("hidden");
    fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        console.log(data);
        if (data.status === 422) {
          this.errorMessage.classList.remove("hidden");
          this.errorMessage.textContent = data.message;
          setTimeout(() => {
            this.errorMessage.classList.add("hidden");
            this.errorMessage.textContent = "";
          }, 5000);
          return;
        }
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:rerender", {
            detail: data,
            bubbles: true,
          }),
        );
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        this.loading.classList.add("hidden");
      });
  }
}

customElements.define("cart-actions", CartActions);

class ProgressBar extends HTMLElement {
  constructor() {
    super();
    this.thresholds = this.getAttribute("data-threshold")
      .split(",")
      .map(Number);

    this.textThreshold = this.getAttribute("data-text-threshold");
    this.textCompleted = this.getAttribute("data-text-completed");

    this.progressBarInner = this.querySelector(".progress-bar-inner");
    this.progressBarText = this.querySelector(".progress-bar-text");
  }
  connectedCallback() {
    this.updateProgressBar();

    console.log(this.thresholds);

    document.addEventListener(
      "cart:rerender",
      this.updateProgressBar.bind(this),
    );
  }

  async getCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const cart = await response.json();
      return cart; // contains items, total_price, etc.
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  }

  calculateSegmentedProgress(total) {
    const thresholds = this.thresholds;
    const segments = thresholds.length - 1; // number of segments

    // If total exceeds last threshold, fill 100%
    if (total >= thresholds[thresholds.length - 1]) return 100;

    for (let i = 1; i < thresholds.length; i++) {
      if (total <= thresholds[i]) {
        const prevIndex = i - 1;
        const progressInSegment =
          (total - thresholds[prevIndex]) /
          (thresholds[i] - thresholds[prevIndex]);
        return ((prevIndex + progressInSegment) / segments) * 100;
      }
    }

    return 0;
  }

  async updateProgressBar() {
    const cart = await this.getCart();

    if (cart.item_count > 0) {
      this.classList.remove("hidden");
      setTimeout(() => {
        this.classList.remove("invisible");
        this.classList.remove("opacity-0");
      }, 400);
    } else {
      setTimeout(() => {
        this.classList.add("invisible");
        this.classList.add("opacity-0");
        this.classList.add("hidden");
      }, 100);
    }

    let threshold = this.thresholds.find(
      (threshold) => cart.total_price < threshold,
    );

    let thresholdIndex;
    if (threshold == undefined) {
      thresholdIndex = this.thresholds.length;
    } else {
      thresholdIndex = this.thresholds.indexOf(threshold);
    }

    this.querySelectorAll("[data-step-index]").forEach((step, index) => {
      if (index < thresholdIndex) {
        step.classList.remove("bg-primary-contrast");
        step.classList.add("bg-border-title");
        step.querySelector("svg").classList.remove("hidden");
      } else {
        step.classList.remove("bg-border-title");
        step.classList.add("bg-primary-contrast");
        step.querySelector("svg").classList.add("hidden");
      }
    });

    if (cart.total_price < threshold) {
      this.progressBarText.textContent = this.textThreshold.replace(
        "{price}",
        Shopify.customFormatMoney(threshold - cart.total_price, "{{amount}}"),
      );
    } else {
      this.progressBarText.textContent = this.textCompleted;
    }
    const percent = this.calculateSegmentedProgress(cart.total_price);
    this.progressBarInner.style.width = `${percent}%`;
  }
}

customElements.define("progress-bar", ProgressBar);

class CartUpsell extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.initSlider();
  }
  initSlider() {
    this.swiper = new Swiper(this.querySelector(".cart-upsell__products"), {
      slidesPerView: "auto",
      spaceBetween: 10,
      loop: true,
      speed: 1000,
      freeMode: true,
      modules: [Navigation],
      navigation: {
        prevEl: this.querySelector(".cart-upsell__prev"),
        nextEl: this.querySelector(".cart-upsell__next"),
      },
    });
  }
}

customElements.define("cart-upsell", CartUpsell);

class CartSubscriptions extends HTMLElement {
  constructor() {
    super();
    this.openButton = this.querySelector("[data-open-subscriptions]");
    this.subscriptionsList = this.querySelector("[data-subscriptions-list]");
    this.plans = this.querySelectorAll("input[type='radio']");
    this.loading = this.closest(".cart-drawer__item").querySelector(
      ".cart-drawer__item-loading",
    );
  }
  connectedCallback() {
    this.handlePlanChange = this.handlePlanChange.bind(this);

    this.openButton?.addEventListener(
      "click",
      this.openSubscriptions.bind(this),
    );
    this.plans.forEach((plan) => {
      plan.addEventListener("change", this.handlePlanChange);
    });
  }
  disconnectedCallback() {
    this.plans.forEach((plan) => {
      plan.removeEventListener("change", this.handlePlanChange);
    });
  }
  handleUpgrade(event) {
    event.preventDefault();
    console.log("upgrade");
  }
  openSubscriptions() {
    this.openButton.classList.toggle("active");

    if (this.subscriptionsList.style.maxHeight === "0px") {
      this.subscriptionsList.style.maxHeight =
        this.subscriptionsList.scrollHeight + "px";
    } else {
      this.subscriptionsList.style.maxHeight = "0px";
    }
  }
  handlePlanChange(event) {
    console.log("plan changed", event.target.value);
    this.loading.classList.remove("hidden");
    function toNumber(value) {
      if (value === null || value === "null" || value === "") {
        return null;
      }

      const num = Number(value);
      return Number.isNaN(num) ? null : num;
    }

    let data = {
      line: this.dataset.line,
      quantity: this.dataset.quantity,
      selling_plan: toNumber(event.target.value),
      sections: "cart-drawer,custom-cart-count",
    };

    fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:rerender", {
            detail: data,
            bubbles: true,
          }),
        );
      })
      .catch((error) => {
        console.error("Error:", error);
      });
    this.openButton?.click();
  }
}

customElements.define("cart-subscriptions", CartSubscriptions);
